/**
 * Centralised workload data service.
 *
 * Single source of truth for fetching capacity schedules, availability,
 * board items, and computing per-user per-day workload entries.
 *
 * Consumers (scheduler, reports, progress timesheet) call this service
 * instead of independently fetching + computing — eliminating duplicated
 * logic and the bug where extractScheduledWork() was called without
 * user schedules / leave lookups.
 *
 * This is a plain TypeScript module (not a React hook).
 */

import {
  getCapacitySchemes,
  getSchemeAssignments,
  type DaySchedule,
  DEFAULT_DAY_SCHEDULE,
} from '../api/capacity.api';
import { fetchAvailability } from '../api/report.api';
import { listBoardConfigs, getBoardConfig, getBoardConfigsBulk, type BoardConfig } from '../api/board-config.api';
import { getAppSettings } from '../api/settings.api';
import { collectMappedColumnIds, fetchBoardsByIds, fetchFilteredBoardItems, fetchSubitemsBoardInfo } from '../api/monday.api';
import {
  type BoardItemsData,
  type AvailabilityResponse,
} from '../utils/report-calculation';
import { computeHoursPerDay } from '../utils/workload-distribution';

// ═══════════════════════════════════════════════════════════════════════════════
// Public types
// ═══════════════════════════════════════════════════════════════════════════════

export interface WorkloadDataRequest {
  startDate: string;
  endDate: string;
  userIds: string[];
  boardIds?: string[];
  /** Skip fetching these boards — the caller already has the data */
  preloadedBoardsData?: BoardItemsData[];
  /** Skip item fetching entirely (scheduler only needs capacity data) */
  skipItems?: boolean;
  /** AbortSignal to cancel in-flight HTTP requests */
  signal?: AbortSignal;
  /** Per-item total worklog hours. When provided, uses remaining estimate mode
   *  (same as scheduler): past days show 0h, future days show remaining = estimate - worklogs. */
  worklogsByItem?: Map<string, number>;
}

export interface WorkloadDataResult {
  entries: UserDayEntry[];
  /** Raw schedules map, exposed for scheduler's computeWorkloadPure() */
  userSchedules: Map<string, Record<string, number>>;
  /** Raw leave lookups, exposed for scheduler's computeWorkloadPure() */
  userLeaveLookups: Map<string, Map<string, 'full' | 'am' | 'pm'>>;
}

export interface UserDayEntry {
  userId: string;
  date: string;

  // ── Capacity side (from backend availability endpoint) ──
  capacityHours: number;
  holidayHours: number;
  leaveHours: number;
  availableHours: number;
  isHoliday: boolean;
  isLeave: boolean;
  isWeekend: boolean;
  leaveDayType: 'full' | 'am' | 'pm' | null;

  // ── Planned side (from monday.com items + correct workHourFormat) ──
  scheduledHours: number;
  itemCount: number;
  items: UserDayItemEntry[];
}

export interface UserDayItemEntry {
  boardId: string;
  boardName: string;
  itemId: string;
  itemName: string;
  hours: number;
  /** Original total hours for the item (before per-day distribution). Used by reports for remaining estimate. */
  totalItemHours?: number;
  /** Work hour format of the item */
  workHourFormat?: 'per_day' | 'total';
  /** Number of assignees the item's hours are split across */
  assigneeCount?: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Shared remaining estimate calculations
// Used by BOTH the scheduler workload indicator AND reports.
// RULE: Never duplicate these formulas — always call these functions.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Compute the remaining hours per person for a total-format item.
 *
 * Formula: max(0, originalTotal - totalWorklogs) / assigneeCount
 *
 * @param originalTotalHours - Original item estimate (before per-person split)
 * @param totalWorklogs      - Total worklogs across all users for this item
 * @param assigneeCount      - Number of assignees (1 if no split)
 * @returns Remaining hours for one person
 */
export function computeRemainingPerPerson(
  originalTotalHours: number,
  totalWorklogs: number,
  assigneeCount: number,
): number {
  const remaining = Math.max(0, originalTotalHours - totalWorklogs);
  return assigneeCount > 1 ? remaining / assigneeCount : remaining;
}

/**
 * Compute the scaling factor to apply to distributed daily hours
 * to reflect remaining work instead of original estimate.
 *
 * Used by reports where hours are already distributed per-day.
 * The scheduler uses computeRemainingPerPerson directly.
 *
 * @param originalTotalHours - Original item estimate (before split)
 * @param totalWorklogs      - Total worklogs for this item
 * @param assigneeCount      - Number of assignees
 * @returns Scale factor (0..1) to multiply distributed daily hours by
 */
export function computeRemainingScale(
  originalTotalHours: number,
  totalWorklogs: number,
  assigneeCount: number,
): number {
  const originalPerPerson = assigneeCount > 1
    ? originalTotalHours / assigneeCount
    : originalTotalHours;
  if (originalPerPerson <= 0) return 0;
  const remainingPerPerson = computeRemainingPerPerson(originalTotalHours, totalWorklogs, assigneeCount);
  return remainingPerPerson / originalPerPerson;
}

/**
 * Check if an item is overlogged (worklogs exceed original estimate).
 * Always compares against the UNSPLIT original total.
 */
export function isItemOverlogged(
  originalTotalHours: number,
  totalWorklogs: number,
): boolean {
  return totalWorklogs > originalTotalHours;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Capacity schedule cache (extracted from useWorkload.ts)
// ═══════════════════════════════════════════════════════════════════════════════

let cachedScheduleMap: Map<string, Record<string, number>> | null = null;
let cachedDefaultSchedule: DaySchedule | null = null;
let scheduleCacheTimestamp = 0;
const SCHEDULE_CACHE_TTL_MS = 60_000; // 1 minute
/** In-flight fetch promise — prevents concurrent callers from duplicating requests */
let scheduleFetchPromise: Promise<{ userSchedules: Map<string, Record<string, number>>; defaultSchedule: DaySchedule }> | null = null;

/** Invalidate the capacity schedule cache — call when user modifies capacity schemes. */
export function invalidateScheduleCache(): void {
  cachedScheduleMap = null;
  cachedDefaultSchedule = null;
  scheduleCacheTimestamp = 0;
}

/**
 * Get the cached default schedule (synchronous).
 * Returns null if no fetch has occurred yet.
 */
export function getDefaultSchedule(): DaySchedule | null {
  return cachedDefaultSchedule;
}

/**
 * Fetch user capacity schedules with module-level 1-minute cache.
 * Returns a Map<userId (string), DaySchedule-as-Record<string,number>>.
 *
 * Extracted from useWorkload.ts getOrFetchUserCapacityMap() — now uses
 * string user IDs throughout.
 */
export async function fetchUserSchedules(
  userIds: string[],
): Promise<{
  userSchedules: Map<string, Record<string, number>>;
  defaultSchedule: DaySchedule;
}> {
  const now = Date.now();
  const cacheValid =
    cachedScheduleMap !== null &&
    now - scheduleCacheTimestamp < SCHEDULE_CACHE_TTL_MS;

  if (cacheValid) {
    const map = cachedScheduleMap!;
    const defSchedule = cachedDefaultSchedule ?? DEFAULT_DAY_SCHEDULE;
    // Ensure all requested users have an entry
    for (const userId of userIds) {
      if (!map.has(userId)) {
        map.set(userId, defSchedule as unknown as Record<string, number>);
      }
    }
    return { userSchedules: map, defaultSchedule: defSchedule };
  }

  // Deduplicate: if a fetch is already in flight, wait for it instead of starting another
  if (scheduleFetchPromise) {
    const result = await scheduleFetchPromise;
    // Ensure all requested users have an entry
    for (const userId of userIds) {
      if (!result.userSchedules.has(userId)) {
        result.userSchedules.set(
          userId,
          result.defaultSchedule as unknown as Record<string, number>,
        );
      }
    }
    return result;
  }

  // Start the fetch and store the promise for deduplication
  scheduleFetchPromise = (async () => {
    const userSchedules = new Map<string, Record<string, number>>();
    let defaultSchedule: DaySchedule = DEFAULT_DAY_SCHEDULE;

    try {
      const schemes = await getCapacitySchemes();
      const defaultScheme = schemes.find((s) => s.is_default);
      if (defaultScheme) defaultSchedule = defaultScheme.schedule;

      // Fetch assignments for all schemes sequentially to avoid complexity budget exhaustion
      const assignmentResults: Array<{ scheme: typeof schemes[number]; assignments: Awaited<ReturnType<typeof getSchemeAssignments>> }> = [];
      for (const scheme of schemes) {
        const assignments = await getSchemeAssignments(scheme.id);
        assignmentResults.push({ scheme, assignments });
      }

      // Build user → scheme schedule mapping (string user IDs)
      for (const { scheme, assignments } of assignmentResults) {
        for (const assignment of assignments) {
          if (assignment.monday_user_id) {
            userSchedules.set(
              String(assignment.monday_user_id),
              scheme.schedule as unknown as Record<string, number>,
            );
          }
        }
      }

      return { userSchedules, defaultSchedule };
    } finally {
      scheduleFetchPromise = null;
    }
  })();

  let result: { userSchedules: Map<string, Record<string, number>>; defaultSchedule: DaySchedule };
  try {
    result = await scheduleFetchPromise;
  } catch {
    // If capacity schemes fail to load, use default 8h schedule for everyone
    const fallbackMap = new Map<string, Record<string, number>>();
    for (const userId of userIds) {
      fallbackMap.set(userId, DEFAULT_DAY_SCHEDULE as unknown as Record<string, number>);
    }
    return { userSchedules: fallbackMap, defaultSchedule: DEFAULT_DAY_SCHEDULE };
  }

  // For users without a specific assignment, use the default scheme
  const { userSchedules, defaultSchedule } = result;
  for (const userId of userIds) {
    if (!userSchedules.has(userId)) {
      userSchedules.set(
        userId,
        defaultSchedule as unknown as Record<string, number>,
      );
    }
  }

  // Update cache
  cachedScheduleMap = userSchedules;
  cachedDefaultSchedule = defaultSchedule;
  scheduleCacheTimestamp = now;

  return { userSchedules, defaultSchedule };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Leave lookup builder
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Build per-user leave lookups from the backend availability response.
 *
 * The backend already computes isLeave + leaveHours per day.
 * We derive the day type:
 * - leaveHours === capacityHours → 'full'
 * - leaveHours > 0 && leaveHours < capacityHours → 'am' (half-day)
 * - otherwise → no leave
 */
function buildUserLeaveLookups(
  availability: AvailabilityResponse,
): Map<string, Map<string, 'full' | 'am' | 'pm'>> {
  const result = new Map<string, Map<string, 'full' | 'am' | 'pm'>>();

  for (const userAvail of availability.users) {
    const leaveLookup = new Map<string, 'full' | 'am' | 'pm'>();

    for (const day of userAvail.days) {
      if (!day.isLeave || day.leaveHours <= 0) continue;

      if (day.leaveHours >= day.capacityHours) {
        leaveLookup.set(day.date, 'full');
      } else {
        // Backend uses capacity_type 'half_day' → maps to 'am' per constraint
        leaveLookup.set(day.date, 'am');
      }
    }

    if (leaveLookup.size > 0) {
      result.set(userAvail.userId, leaveLookup);
    }
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Board data fetcher
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fetch board configs + filtered items for specified boards.
 * Consolidates duplicated logic from ReportConfigDialog and useProgressData.
 */
async function fetchBoardsItemsData(
  userIds: string[],
  startDate: string,
  endDate: string,
  boardIds?: string[],
  preloadedBoardsData?: BoardItemsData[],
): Promise<BoardItemsData[]> {
  // Start with preloaded data
  const result: BoardItemsData[] = preloadedBoardsData
    ? [...preloadedBoardsData]
    : [];
  const preloadedBoardIds = new Set(result.map((b) => b.boardId));

  // Determine which boards to fetch
  let targetBoardIds: string[];

  if (boardIds && boardIds.length > 0) {
    // Specific boards requested — skip already-preloaded ones
    targetBoardIds = boardIds.filter((id) => !preloadedBoardIds.has(id));
  } else {
    // No specific boards → fetch up to 150 newest configured boards
    const configResp = await listBoardConfigs(1, 150);
    const validConfigs = configResp.items.filter((c) => {
      const mainValid =
        c.fieldMapping.timelineColumnId && c.fieldMapping.assigneesColumnId;
      const subValid =
        c.subitemFieldMapping?.timelineColumnId &&
        c.subitemFieldMapping?.assigneesColumnId;
      const level = c.itemLevel ?? 'main';
      if (level === 'main') return mainValid;
      if (level === 'sub') return subValid;
      return mainValid || subValid; // 'both'
    });
    targetBoardIds = validConfigs
      .map((c) => String(c.boardId))
      .filter((id) => !preloadedBoardIds.has(id));
  }

  // Fetch account-level defaults once so board configs with null values get resolved
  let accountPerPersonDistribution = false;
  const needsAccountDefault = result.some((b) => b.config.perPersonDistribution === null) ||
    targetBoardIds.length > 0;
  if (needsAccountDefault) {
    try {
      const appSettings = await getAppSettings();
      accountPerPersonDistribution = appSettings.perPersonDistribution;
    } catch {
      // Fall back to false if settings unavailable
    }
  }

  // Resolve preloaded board configs that have null perPersonDistribution
  for (const entry of result) {
    if (entry.config.perPersonDistribution === null) {
      entry.config = { ...entry.config, perPersonDistribution: accountPerPersonDistribution };
    }
  }

  if (targetBoardIds.length === 0) return result;

  // Pre-fetch all board configs in a single request so individual getBoardConfig
  // calls inside the parallel loop are instant cache hits (instead of N serial
  // HTTP requests to our own backend).
  await getBoardConfigsBulk(targetBoardIds.map(Number));

  // Fetch items from boards with bounded parallelism. Per-request complexity
  // is small (page limit 100 + column_values(ids: [...])), so 8 concurrent
  // requests stay well under monday.com's complexity budget and concurrency cap.
  //
  // Subitems are fetched from the subitems board directly (same pattern as
  // scheduler + reports). This allows timeline + people filtering on main items
  // even in 'both' mode, and independent timeline filtering on subitems —
  // previously the main fetch was unfiltered because subitems were nested.
  const MAX_CONCURRENT = 8;
  for (let i = 0; i < targetBoardIds.length; i += MAX_CONCURRENT) {
    const batch = targetBoardIds.slice(i, i + MAX_CONCURRENT);
    const batchResults = await Promise.all(
      batch.map(async (boardId) => {
        try {
          const config = await getBoardConfig(Number(boardId));

          // Resolve null board-level settings from account defaults
          const resolvedConfig: BoardConfig = {
            ...config,
            perPersonDistribution: config.perPersonDistribution ?? accountPerPersonDistribution,
          };

          const itemLevel = resolvedConfig.itemLevel ?? 'main';
          const mainColumnIds = collectMappedColumnIds(resolvedConfig.fieldMapping);
          let boardName = '';
          let items: import('../api/monday.api').MondayItem[] = [];
          let subItems: import('../api/monday.api').MondayItem[] | undefined;

          // Fetch main items (when level is 'main' or 'both') with timeline + people filter.
          // includeSubitems: false — subitems come from a separate subitems-board fetch below.
          if ((itemLevel === 'main' || itemLevel === 'both') && resolvedConfig.fieldMapping.timelineColumnId) {
            const filter = {
              timeline: {
                columnId: resolvedConfig.fieldMapping.timelineColumnId,
                startDate,
                endDate,
              },
              people: resolvedConfig.fieldMapping.assigneesColumnId
                ? { columnId: resolvedConfig.fieldMapping.assigneesColumnId, userIds }
                : undefined,
            };
            const fetched = await fetchFilteredBoardItems(
              Number(boardId),
              filter,
              undefined,
              false,
              mainColumnIds,
            );
            boardName = fetched.boardName;
            items = fetched.items;
          }

          // Fetch subitems from the subitems board directly (when level is 'sub' or 'both').
          if (
            (itemLevel === 'sub' || itemLevel === 'both')
            && resolvedConfig.subitemFieldMapping
            && resolvedConfig.subitemFieldMapping.timelineColumnId
          ) {
            const subBoardInfo = await fetchSubitemsBoardInfo(Number(boardId));
            const subBoardId = subBoardInfo?.subitemsBoardId ?? 0;
            if (subBoardId) {
              const subMapping = resolvedConfig.subitemFieldMapping;
              const subColumnIds = collectMappedColumnIds(subMapping);
              const subFilter = {
                timeline: {
                  columnId: subMapping.timelineColumnId!,
                  startDate,
                  endDate,
                },
              };
              const { items: fetchedSubItems, boardName: subBoardName } = await fetchFilteredBoardItems(
                subBoardId,
                subFilter,
                undefined,
                false,
                subColumnIds,
              );
              subItems = fetchedSubItems;
              // If main fetch was skipped (sub-only mode), resolve the PARENT
              // board name — subBoardName is the subitems board name (e.g.
              // "Subitems of Project Alpha") which isn't user-friendly.
              if (!boardName) {
                try {
                  const boards = await fetchBoardsByIds([boardId]);
                  boardName = boards[0]?.name || boardId;
                } catch {
                  boardName = boardId;
                }
              }
            }
          }

          return { boardId, boardName, items, subItems, config: resolvedConfig } as BoardItemsData;
        } catch (err) {
          console.warn(`[workload-data] Failed to fetch board ${boardId}:`, err);
          return null;
        }
      }),
    );
    for (const r of batchResults) {
      if (r) result.push(r);
    }
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Entry builder
// ═══════════════════════════════════════════════════════════════════════════════

// buildEntries removed — fetchWorkloadData now uses computeSchedulerWorkload
// directly, which is the single source of truth for hour distribution.

// ═══════════════════════════════════════════════════════════════════════════════
// Main entry point
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Convert raw monday.com board items into SchedulerItem[] format.
 *
 * This bridges the gap between the report/timesheet path (which fetches raw
 * items via monday SDK) and the scheduler computation (which expects
 * SchedulerItem[]). Respects itemLevel, perPersonDistribution, and field
 * mappings per board.
 */
export function convertBoardItemsToSchedulerItems(
  boardsData: BoardItemsData[],
  userIdSet: Set<string>,
): SchedulerItem[] {
  const items: SchedulerItem[] = [];

  for (const { boardId, boardName, items: mondayItems, subItems, config } of boardsData) {
    const perPerson = config.perPersonDistribution ?? false;
    const itemLevel = config.itemLevel ?? 'main';

    const processItem = (
      item: { id: string; name: string; column_values: Array<{ id: string; type: string; text: string; value: string | null }> },
      mapping: { timelineColumnId: string | null; assigneesColumnId: string | null; scheduledHoursColumnId: string | null; scheduledHoursPerDayColumnId: string | null },
    ) => {
      const timelineCol = item.column_values.find((c) => c.id === mapping.timelineColumnId);
      const timeline = parseTimelineValue(timelineCol?.value ?? null);
      if (!timeline) return;

      const peopleCol = item.column_values.find((c) => c.id === mapping.assigneesColumnId);
      const personIds = parseAssigneesValue(peopleCol?.value ?? null);
      const relevant = personIds.filter((pid) => userIdSet.has(pid));
      if (relevant.length === 0) return;

      let hoursTotal = 0;
      let hoursPerDay = 0;
      if (mapping.scheduledHoursColumnId) {
        const col = item.column_values.find((c) => c.id === mapping.scheduledHoursColumnId);
        hoursTotal = parseHoursText(col?.text);
      }
      if (mapping.scheduledHoursPerDayColumnId) {
        const col = item.column_values.find((c) => c.id === mapping.scheduledHoursPerDayColumnId);
        hoursPerDay = parseHoursText(col?.text);
      }

      const workHourFormat: 'per_day' | 'total' = hoursPerDay > 0 ? 'per_day' : 'total';
      const rawHours = hoursPerDay > 0 ? hoursPerDay : hoursTotal;
      const totalAssignees = relevant.length;

      for (const userId of relevant) {
        const perPersonHours = (perPerson && totalAssignees > 1)
          ? Math.round((rawHours / totalAssignees) * 100) / 100
          : rawHours;

        items.push({
          mondayItemId: Number(item.id),
          assigneeId: Number(userId),
          startDate: timeline.from,
          endDate: timeline.to,
          hours: perPersonHours,
          workHourFormat,
          originalTotalHours: rawHours,
          assigneeCount: totalAssignees,
          boardId: Number(boardId),
          boardName,
          itemName: item.name,
        });
      }
    };

    // Main items
    if (itemLevel === 'main' || itemLevel === 'both') {
      const mapping = config.fieldMapping;
      if (mapping.timelineColumnId && mapping.assigneesColumnId) {
        for (const item of mondayItems) processItem(item, mapping);
      }
    }

    // Subitems — use the separately-fetched flat subItems array when available,
    // falling back to nested item.subitems for backwards compatibility with
    // any preloaded data that still uses the old nested format.
    if (itemLevel === 'sub' || itemLevel === 'both') {
      const subMapping = config.subitemFieldMapping;
      if (subMapping?.timelineColumnId && subMapping?.assigneesColumnId) {
        if (subItems && subItems.length > 0) {
          for (const sub of subItems) processItem(sub, subMapping);
        } else {
          for (const item of mondayItems) {
            for (const sub of item.subitems ?? []) processItem(sub, subMapping);
          }
        }
      }
    }
  }

  return items;
}

// ── Inline parsers (same logic as report-calculation.ts / schedule.api.ts) ──

function parseTimelineValue(value: string | null): { from: string; to: string } | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as { from?: string; to?: string };
    if (parsed.from && parsed.to) return { from: parsed.from, to: parsed.to };
  } catch { /* ignore */ }
  return null;
}

function parseAssigneesValue(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as { personsAndTeams?: Array<{ id: number; kind: string }> };
    return (parsed.personsAndTeams ?? [])
      .filter((e) => e.kind === 'person')
      .map((e) => String(e.id));
  } catch { return []; }
}

function parseHoursText(text: string | undefined | null): number {
  if (!text) return 0;
  const num = parseFloat(text);
  return isNaN(num) ? 0 : num;
}

/**
 * Fetch all workload data for the given request.
 *
 * Orchestrates:
 * 1. User capacity schedules (from getCapacitySchemes + getSchemeAssignments)
 * 2. Availability (from backend POST /reports/availability)
 * 3. Board items (from monday.com via SDK)
 * 4. computeSchedulerWorkload() — single source of truth for hour distribution
 * 5. Merge scheduler result with availability into UserDayEntry[]
 */
export async function fetchWorkloadData(
  request: WorkloadDataRequest,
): Promise<WorkloadDataResult> {
  const {
    startDate,
    endDate,
    userIds,
    boardIds,
    preloadedBoardsData,
    skipItems,
    signal,
    worklogsByItem,
  } = request;

  // 1. Fetch schedules + availability sequentially to avoid complexity budget exhaustion
  const { userSchedules } = await fetchUserSchedules(userIds);
  const availability = await fetchAvailability({ userIds, startDate, endDate }, signal);

  // 2. Build leave lookups from availability response
  const userLeaveLookups = buildUserLeaveLookups(availability);

  // 3. Convert board items to SchedulerItem[] and compute via shared scheduler logic
  let schedulerResult: SchedulerWorkloadResult | undefined;
  if (!skipItems) {
    const boardsData = await fetchBoardsItemsData(
      userIds, startDate, endDate, boardIds, preloadedBoardsData,
    );

    const userIdSet = new Set(userIds);
    const schedulerItems = convertBoardItemsToSchedulerItems(boardsData, userIdSet);

    // Build numeric capacity map for computeSchedulerWorkload
    const numericCapacityMap = new Map<number, Record<string, number>>();
    for (const [uid, sched] of userSchedules) {
      numericCapacityMap.set(Number(uid), sched);
    }

    // Build leave entries from availability
    const approvedLeaves: SchedulerLeaveEntry[] = [];
    for (const [userId, leaveLookup] of userLeaveLookups) {
      for (const [date, dayType] of leaveLookup) {
        approvedLeaves.push({
          assigneeId: Number(userId),
          startDate: date,
          endDate: date,
          dayType,
          status: 'Approved',
        });
      }
    }

    // Build holiday entries from availability
    const holidays: SchedulerHolidayEntry[] = [];
    for (const userAvail of availability.users) {
      for (const day of userAvail.days) {
        if (day.isHoliday) {
          holidays.push({ date: day.date, name: 'Holiday', assignedUserIds: [userAvail.userId] });
        }
      }
    }

    const allUserIds = new Set(userIds.map(Number));

    schedulerResult = computeSchedulerWorkload({
      items: schedulerItems,
      approvedLeaves,
      holidays,
      allUserIds,
      dateRangeStart: startDate,
      dateRangeEnd: endDate,
      perPersonDistribution: false, // already split in convertBoardItemsToSchedulerItems
      trackItemBreakdown: true,
      // When worklogs are provided, use remaining estimate mode (same as scheduler):
      // past days → 0h, future days → remaining estimate distributed evenly
      ...(worklogsByItem ? { indicatorType: 'remaining_estimate', worklogsByItem } : {}),
    }, numericCapacityMap);
  }

  // 4. Merge scheduler result with availability data into UserDayEntry[]
  // Index scheduler entries for O(1) lookup
  const schedulerEntryMap = new Map<string, SchedulerWorkloadEntry>();
  if (schedulerResult) {
    for (const e of schedulerResult.entries) {
      schedulerEntryMap.set(`${e.userId}_${e.date}`, e);
    }
  }

  const entries: UserDayEntry[] = [];
  for (const userAvail of availability.users) {
    const userLeaves = userLeaveLookups.get(userAvail.userId);
    const numericUserId = Number(userAvail.userId);

    for (const day of userAvail.days) {
      const mapKey = `${numericUserId}_${day.date}`;
      const breakdown = schedulerResult?.itemBreakdown?.get(mapKey) ?? [];
      const scheduledHours = schedulerEntryMap.get(mapKey)?.scheduledHours ?? 0;

      entries.push({
        userId: userAvail.userId,
        date: day.date,
        capacityHours: day.capacityHours,
        holidayHours: day.holidayHours,
        leaveHours: day.leaveHours,
        availableHours: day.availableHours,
        isHoliday: day.isHoliday,
        isLeave: day.isLeave,
        isWeekend: day.isWeekend,
        leaveDayType: userLeaves?.get(day.date) ?? null,
        scheduledHours,
        itemCount: breakdown.length,
        items: breakdown.map(({ item, hoursOnDay }) => ({
          boardId: String(item.boardId ?? ''),
          boardName: item.boardName ?? '',
          itemId: String(item.mondayItemId),
          itemName: item.itemName ?? '',
          hours: hoursOnDay,
          totalItemHours: item.originalTotalHours,
          workHourFormat: item.workHourFormat,
          assigneeCount: item.assigneeCount,
        })),
      });
    }
  }

  return { entries, userSchedules, userLeaveLookups };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Scheduler workload computation (from local ScheduleItem[] + LeaveEntry[])
// ═══════════════════════════════════════════════════════════════════════════════

// Day-of-week index → key mapping (getDay() returns 0=Sun … 6=Sat)
const DOW_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/** Minimal leave entry shape required by the scheduler computation. */
export interface SchedulerLeaveEntry {
  assigneeId: number;
  startDate: string;
  endDate: string;
  dayType: 'full' | 'am' | 'pm';
  status: string;
}

/** Minimal schedule item shape required by the scheduler computation. */
export interface SchedulerItem {
  mondayItemId: number;
  assigneeId: number;
  startDate: string;
  endDate: string;
  /** Per-person hours (already split by assignee count if perPersonDistribution is on) */
  hours: number;
  workHourFormat: 'per_day' | 'total';
  /** Original total hours before per-person split (from monday.com column). Used for remaining estimate. */
  originalTotalHours?: number;
  /** Number of assignees the hours are split across */
  assigneeCount?: number;
  /** Board metadata (set when items come from report/timesheet path) */
  boardId?: number;
  boardName?: string;
  itemName?: string;
}

/** Minimal holiday entry shape required by the scheduler computation. */
export interface SchedulerHolidayEntry {
  date: string;         // YYYY-MM-DD
  name: string;
  assignedUserIds: string[];  // monday user IDs this holiday applies to
}

export interface SchedulerWorkloadInput {
  items: SchedulerItem[];
  approvedLeaves: SchedulerLeaveEntry[];
  holidays?: SchedulerHolidayEntry[];
  allUserIds: Set<number>;
  dateRangeStart: string;
  dateRangeEnd: string;
  perPersonDistribution: boolean;
  /** Active indicator type — controls how hours are computed for new modes */
  indicatorType?: string;
  /** Per-user per-day worklog hours, keyed as "userId_YYYY-MM-DD" → totalHours.
   *  Used by worklog_workload mode to display worklogs on past days. */
  worklogsByUserDate?: Map<string, number>;
  /** Per-item total worklog hours, keyed as mondayItemId (string) → totalHours.
   *  Used to compute remaining estimate = totalEstimate - totalWorklogs. */
  worklogsByItem?: Map<string, number>;
  /** When true, track per-item breakdown per user per day (for reports/timesheets). */
  trackItemBreakdown?: boolean;
}

/** Per-user per-day workload entry produced by the scheduler computation. */
export interface SchedulerWorkloadEntry {
  userId: number;
  date: string;
  scheduledHours: number;
  capacityHours: number;
  isHoliday: boolean;
  holidayHours: number;
  isLeave: boolean;
  leaveDayType: 'full' | 'am' | 'pm' | null;
  leaveHours: number;
  availableHours: number;
  /** True when items exist on a full-day leave (hours were redistributed but conflict persists) */
  hasLeaveConflict: boolean;
  /** Hours logged via worklogs on this date (for remaining_estimate and worklog_workload modes) */
  worklogHours?: number;
  /** True for past days in remaining_estimate mode (UI renders "–") */
  isPastDay?: boolean;
}

export interface SchedulerIssueCountEntry {
  userId: number;
  date: string;
  issueCount: number;
}

/** Per-item hours on a specific day, used when trackItemBreakdown is enabled. */
export interface ItemDayBreakdown {
  item: SchedulerItem;
  hoursOnDay: number;
}

export interface SchedulerWorkloadResult {
  entries: SchedulerWorkloadEntry[];
  issueEntries: SchedulerIssueCountEntry[];
  /** Per-user per-day item breakdown. Key: "userId_YYYY-MM-DD". Only populated when trackItemBreakdown is true. */
  itemBreakdown?: Map<string, ItemDayBreakdown[]>;
}

interface LeaveInfo {
  dayType: 'full' | 'am' | 'pm';
  leaveHours: number;
}

/**
 * Single source of truth for workload calculation.
 *
 * Pure computation (no async, no side effects) that distributes item hours
 * across working days, respecting capacity schemes, leaves, and holidays.
 *
 * Used by TWO callers — both share this exact logic:
 *
 * 1. **Scheduler (Gantt view)** — called from `useWorkload.ts` with items
 *    from the schedule store. Does NOT pass `trackItemBreakdown`.
 *
 * 2. **Reports & Timesheets** — called from `fetchWorkloadData()` with items
 *    converted from monday.com API via `convertBoardItemsToSchedulerItems()`.
 *    Passes `trackItemBreakdown: true` to get per-item hours breakdown and
 *    optionally `indicatorType: 'remaining_estimate'` + `worklogsByItem`
 *    for remaining estimate mode.
 *
 * The `trackItemBreakdown` flag is the only difference between the two paths.
 * When false/undefined (scheduler), all breakdown-related code is skipped —
 * zero performance or behavioral impact on the scheduler.
 */
export function computeSchedulerWorkload(
  input: SchedulerWorkloadInput,
  userCapacityMap: Map<number, Record<string, number>>,
): SchedulerWorkloadResult {
  const {
    items, approvedLeaves, holidays,
    allUserIds,
    dateRangeStart, dateRangeEnd, perPersonDistribution,
    indicatorType, worklogsByUserDate, worklogsByItem,
    trackItemBreakdown,
  } = input;

  const isRemainingMode = indicatorType === 'remaining_estimate' || indicatorType === 'worklog_workload' || indicatorType === 'availability';

  const defaultSched = DEFAULT_DAY_SCHEDULE as unknown as Record<string, number>;

  // Today's date string for past/future boundary
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // ── 0. Build holiday lookup (userId_date → true) ───────────────────────
  const holidayLookup = new Set<string>();
  if (holidays) {
    for (const holiday of holidays) {
      for (const userIdStr of holiday.assignedUserIds) {
        holidayLookup.add(`${userIdStr}_${holiday.date}`);
      }
    }
  }

  // ── 1. Build leave lookup ──────────────────────────────────────────────
  const leaveLookup = new Map<string, LeaveInfo>();
  for (const leave of approvedLeaves) {
    if (leave.status !== 'Approved') continue;
    const leaveStart = new Date(leave.startDate + 'T00:00:00');
    const leaveEnd = new Date(leave.endDate + 'T00:00:00');
    const current = new Date(leaveStart);
    while (current <= leaveEnd) {
      const dow = DOW_KEYS[current.getDay()];
      const userSchedule = userCapacityMap.get(leave.assigneeId) ?? defaultSched;
      const dayCap = (userSchedule[dow] ?? 0);
      if (dayCap <= 0) { current.setDate(current.getDate() + 1); continue; }
      const leaveHrs = leave.dayType === 'full' ? dayCap : dayCap / 2;
      const y = current.getFullYear();
      const m = String(current.getMonth() + 1).padStart(2, '0');
      const d = String(current.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      const key = `${leave.assigneeId}_${dateStr}`;
      const existing = leaveLookup.get(key);
      if (!existing || (existing.dayType !== 'full' && leave.dayType === 'full')) {
        leaveLookup.set(key, { dayType: leave.dayType, leaveHours: leaveHrs });
      }
      current.setDate(current.getDate() + 1);
    }
  }

  // ── 2. Sum scheduled hours + count issues per user per day ─────────────

  const rangeStart = new Date(dateRangeStart + 'T00:00:00');
  const rangeEnd = new Date(dateRangeEnd + 'T00:00:00');

  // NOTE: Scheduler items already have per-person hours (split in schedule.api.ts),
  // so we do NOT divide by assigneeCount here — that would double-divide.
  // The assigneeCount logic in computeHoursPerDay is only needed by the report
  // path (extractItemWork), which works on raw monday.com column values.

  const scheduledMap = new Map<string, number>();
  const issueCountMap = new Map<string, number>();
  /** Tracks days where items exist on a full-day leave (for conflict detection) */
  const leaveConflictSet = new Set<string>();
  /** Per-item breakdown per user per day (only when trackItemBreakdown is enabled) */
  const itemBreakdownMap = trackItemBreakdown ? new Map<string, ItemDayBreakdown[]>() : undefined;

  // For remaining estimate modes: map to accumulate remaining-estimate-based
  // scheduled hours that only apply to today + future working days.
  const remainingScheduledMap = new Map<string, number>();

  for (const item of items) {
    if (item.assigneeId === 0) continue;

    const itemStart = new Date(item.startDate + 'T00:00:00');
    const itemEnd = new Date(item.endDate + 'T00:00:00');
    const userSchedule = userCapacityMap.get(item.assigneeId) ?? defaultSched;

    // Build per-user leave+holiday map for computeHoursPerDay (only needed for total mode)
    // Holidays are treated as full-day leaves for hour distribution purposes.
    let itemLeaveLookup: Map<string, 'full' | 'am' | 'pm'> | undefined;
    if (item.workHourFormat === 'total') {
      itemLeaveLookup = new Map();
      const cur = new Date(itemStart);
      while (cur <= itemEnd) {
        const y = cur.getFullYear();
        const m = String(cur.getMonth() + 1).padStart(2, '0');
        const d = String(cur.getDate()).padStart(2, '0');
        const dateStr = `${y}-${m}-${d}`;
        // Holidays count as full-day non-working for distribution
        if (holidayLookup.has(`${item.assigneeId}_${dateStr}`)) {
          itemLeaveLookup.set(dateStr, 'full');
        }
        const leaveInfo = leaveLookup.get(`${item.assigneeId}_${dateStr}`);
        if (leaveInfo) {
          itemLeaveLookup.set(dateStr, leaveInfo.dayType);
        }
        cur.setDate(cur.getDate() + 1);
      }
    }

    const hoursPerDay = computeHoursPerDay({
      totalHours: item.hours,
      workHourFormat: item.workHourFormat,
      timelineStart: item.startDate,
      timelineEnd: item.endDate,
      schedule: userSchedule,
      leaveLookup: itemLeaveLookup,
      // Scheduler items already have per-person hours — no additional division needed
    });

    // Standard scheduled hours distribution (used by workload/availability/issues modes)
    const cur = new Date(itemStart);
    while (cur <= itemEnd) {
      const dow = DOW_KEYS[cur.getDay()];
      if ((userSchedule[dow] ?? 0) <= 0) { cur.setDate(cur.getDate() + 1); continue; }
      if (cur < rangeStart || cur > rangeEnd) { cur.setDate(cur.getDate() + 1); continue; }

      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      const leaveInfo = leaveLookup.get(`${item.assigneeId}_${dateStr}`);
      const isHolidayDay = holidayLookup.has(`${item.assigneeId}_${dateStr}`);

      const mapKey = `${item.assigneeId}_${dateStr}`;

      // Holiday days — no work should be scheduled (same as full-day leave)
      if (item.workHourFormat === 'total' && isHolidayDay) {
        if (hoursPerDay === 0 && item.hours > 0) leaveConflictSet.add(mapKey);
        cur.setDate(cur.getDate() + 1);
        continue;
      }

      if (item.workHourFormat === 'total' && leaveInfo?.dayType === 'full') {
        // Hours redistributed to non-leave days by computeHoursPerDay.
        // Only flag a conflict when redistribution was impossible (no working days)
        // AND the item actually has hours to redistribute. Items with 0 hours
        // (no estimate configured) should never trigger a conflict.
        if (hoursPerDay === 0 && item.hours > 0) leaveConflictSet.add(mapKey);
        cur.setDate(cur.getDate() + 1);
        continue;
      }

      scheduledMap.set(mapKey, (scheduledMap.get(mapKey) ?? 0) + hoursPerDay);
      issueCountMap.set(mapKey, (issueCountMap.get(mapKey) ?? 0) + 1);
      // Only populate item breakdown for standard mode — remaining estimate mode
      // populates it in its own distribution loop with correct future-only hours.
      if (itemBreakdownMap && !isRemainingMode) {
        let arr = itemBreakdownMap.get(mapKey);
        if (!arr) { arr = []; itemBreakdownMap.set(mapKey, arr); }
        arr.push({ item, hoursOnDay: hoursPerDay });
      }
      cur.setDate(cur.getDate() + 1);
    }

    // ── Remaining estimate distribution (for remaining_estimate / worklog_workload) ──
    if (isRemainingMode && item.hours > 0) {
      const todayDate = new Date(todayStr + 'T00:00:00');
      const effectiveStart = itemStart > todayDate ? itemStart : todayDate;

      if (effectiveStart <= itemEnd) {
        let remainingPerDay: number;

        if (item.workHourFormat === 'per_day') {
          // Per-day items: simply use the per-day rate for each future working day.
          // Past days don't affect future — the rate is constant.
          remainingPerDay = item.hours;
        } else {
          // Total items: compute remaining = total - worklogs, distribute across future days.
          // Count future working days (today inclusive) for this item
          let futureWorkingDays = 0;
          const futCur = new Date(effectiveStart);
          while (futCur <= itemEnd) {
            const futDow = DOW_KEYS[futCur.getDay()];
            const futY = futCur.getFullYear();
            const futM = String(futCur.getMonth() + 1).padStart(2, '0');
            const futD = String(futCur.getDate()).padStart(2, '0');
            const futDateStr = `${futY}-${futM}-${futD}`;
            const futLeave = leaveLookup.get(`${item.assigneeId}_${futDateStr}`);
            const futHoliday = holidayLookup.has(`${item.assigneeId}_${futDateStr}`);
            if ((userSchedule[futDow] ?? 0) > 0 && !futHoliday && futLeave?.dayType !== 'full') {
              futureWorkingDays += futLeave ? 0.5 : 1;
            }
            futCur.setDate(futCur.getDate() + 1);
          }

          if (futureWorkingDays <= 0) {
            // No future working days — skip distribution
            continue;
          }

          const originalTotal = item.originalTotalHours ?? item.hours;
          // Only split by assignee count when per-person distribution is enabled
          const effectiveAssigneeCount = perPersonDistribution ? (item.assigneeCount ?? 1) : 1;
          const itemWorklogs = worklogsByItem?.get(String(item.mondayItemId)) ?? 0;
          const remainingPerPerson = computeRemainingPerPerson(originalTotal, itemWorklogs, effectiveAssigneeCount);
          remainingPerDay = remainingPerPerson / futureWorkingDays;
        }

        if (remainingPerDay > 0) {
          // Distribute remaining hours to future working days
          const distCur = new Date(effectiveStart);
          while (distCur <= itemEnd) {
            const distDow = DOW_KEYS[distCur.getDay()];
            const distY = distCur.getFullYear();
            const distM = String(distCur.getMonth() + 1).padStart(2, '0');
            const distD = String(distCur.getDate()).padStart(2, '0');
            const distDateStr = `${distY}-${distM}-${distD}`;
            const distLeave = leaveLookup.get(`${item.assigneeId}_${distDateStr}`);
            const distHoliday = holidayLookup.has(`${item.assigneeId}_${distDateStr}`);

            if ((userSchedule[distDow] ?? 0) > 0 && !distHoliday && distLeave?.dayType !== 'full') {
              if (distCur >= rangeStart && distCur <= rangeEnd) {
                const key = `${item.assigneeId}_${distDateStr}`;
                // Half-day leave counts as 0.5 working day → distribute half the per-day amount
                const dayFraction = distLeave ? 0.5 : 1;
                const hoursOnDay = remainingPerDay * dayFraction;
                remainingScheduledMap.set(key, (remainingScheduledMap.get(key) ?? 0) + hoursOnDay);
                if (itemBreakdownMap) {
                  let arr = itemBreakdownMap.get(key);
                  if (!arr) { arr = []; itemBreakdownMap.set(key, arr); }
                  arr.push({ item, hoursOnDay });
                }
              }
            }
            distCur.setDate(distCur.getDate() + 1);
          }
        }
      }
    }
  }

  // ── 3. Build entries ───────────────────────────────────────────────────

  const entries: SchedulerWorkloadEntry[] = [];
  const issueEntries: SchedulerIssueCountEntry[] = [];

  const dayCur = new Date(rangeStart);
  const allDates: string[] = [];
  while (dayCur <= rangeEnd) {
    const y = dayCur.getFullYear();
    const m = String(dayCur.getMonth() + 1).padStart(2, '0');
    const d = String(dayCur.getDate()).padStart(2, '0');
    allDates.push(`${y}-${m}-${d}`);
    dayCur.setDate(dayCur.getDate() + 1);
  }

  for (const userId of allUserIds) {
    const userSchedule = userCapacityMap.get(userId) ?? defaultSched;

    for (const dateStr of allDates) {
      const date = new Date(dateStr + 'T00:00:00');
      const dow = DOW_KEYS[date.getDay()];
      const capacityHours = userSchedule[dow] ?? 0;
      const standardScheduledHours = scheduledMap.get(`${userId}_${dateStr}`) ?? 0;
      const issueCount = issueCountMap.get(`${userId}_${dateStr}`) ?? 0;

      const leaveInfo = leaveLookup.get(`${userId}_${dateStr}`);
      const isLeave = !!leaveInfo;
      const leaveDayType = leaveInfo?.dayType ?? null;
      const leaveHrs = leaveInfo?.leaveHours ?? 0;

      const isHoliday = holidayLookup.has(`${userId}_${dateStr}`);
      const holidayHrs = isHoliday ? capacityHours : 0;

      const availableHours = Math.max(0, capacityHours - holidayHrs - leaveHrs);
      const hasLeaveConflict = leaveConflictSet.has(`${userId}_${dateStr}`);

      const isPastDay = dateStr < todayStr;
      const isToday = dateStr === todayStr;
      const worklogHrs = worklogsByUserDate?.get(`${userId}_${dateStr}`) ?? 0;

      // Determine scheduled hours based on indicator mode
      let scheduledHours: number;
      if (indicatorType === 'remaining_estimate') {
        if (isPastDay) {
          // Past days: show nothing (UI will render "–")
          scheduledHours = 0;
        } else {
          // Today + future: remaining estimate hours
          scheduledHours = remainingScheduledMap.get(`${userId}_${dateStr}`) ?? 0;
        }
      } else if (indicatorType === 'worklog_workload') {
        if (isPastDay) {
          // Past days: show actual worklogs
          scheduledHours = worklogHrs;
        } else if (isToday) {
          // Today: worklogs + remaining estimate
          const remainingToday = remainingScheduledMap.get(`${userId}_${dateStr}`) ?? 0;
          scheduledHours = worklogHrs + remainingToday;
        } else {
          // Future: remaining estimate
          scheduledHours = remainingScheduledMap.get(`${userId}_${dateStr}`) ?? 0;
        }
      } else if (indicatorType === 'availability') {
        // Availability uses remaining estimate for accurate free hours
        if (isPastDay) {
          // Past days: show nothing (UI will render "–" like remaining_estimate)
          scheduledHours = 0;
        } else {
          scheduledHours = remainingScheduledMap.get(`${userId}_${dateStr}`) ?? 0;
        }
      } else {
        // Standard modes (workload, issues)
        scheduledHours = standardScheduledHours;
      }

      entries.push({
        userId,
        date: dateStr,
        scheduledHours: Math.round(scheduledHours * 100) / 100,
        capacityHours,
        isHoliday,
        holidayHours: holidayHrs,
        isLeave,
        leaveDayType,
        leaveHours: leaveHrs,
        availableHours: Math.round(availableHours * 100) / 100,
        hasLeaveConflict,
        worklogHours: isRemainingMode ? Math.round(worklogHrs * 100) / 100 : undefined,
        isPastDay: isRemainingMode ? isPastDay : undefined,
      });
      issueEntries.push({
        userId,
        date: dateStr,
        issueCount,
      });
    }
  }

  return { entries, issueEntries, itemBreakdown: itemBreakdownMap };
}
